import { SelectionPromotion } from '@teko/cart/v2';
import { CheckOutlined, DownOutlined, UpOutlined } from '@teko/ds-icon';
import {
  Body,
  Divider,
  Flex,
  SubTitle,
  ThemeProps,
  styled,
} from '@teko/ds-mobile';
import { trackManually } from '@teko/tracking';
import { DividerStyleEnum } from '@teko/types/dist/shared';
import { requestIdleCallback } from '@teko/utils';
import { useProductDetailState } from 'contexts';
import { t } from 'i18n';
import { useState } from 'react';

interface ISelectionPromotionsProps {
  title: string;
  description?: string;
  promotions?: SelectionPromotion[];
}

const MAX_ITEMS_PROMOTIONS = 3;

export const SelectionPromotions = (props: ISelectionPromotionsProps) => {
  const { title, description, promotions = [] } = props;

  const { product, setSelectedPromotion } = useProductDetailState();
  const [showFullPromotions, setShowFullPromotions] = useState(false);

  const handlePromotionClick = (
    promotion: SelectionPromotion,
    index: number,
  ) => {
    if (!promotion) return;
    if (promotion.isSelected) {
      setSelectedPromotion?.(undefined);
    } else {
      setSelectedPromotion?.(promotion);
    }
    requestIdleCallback(() =>
      trackManually(
        promotion.name as string,
        'optionalPromotion',
        'productDetail',
        JSON.stringify({
          sku: product?.sku,
          action: promotion.isSelected ? 'unselect' : 'select',
          promotionID: promotion.promotionId,
          screenName: 'productDetail',
          index,
        }),
      ),
    );
  };

  const toggleShowFullPromotions = () => {
    setShowFullPromotions((prev) => !prev);
  };

  if (promotions?.length === 0) return null;

  const convertPromotion = showFullPromotions
    ? promotions
    : promotions.slice(0, MAX_ITEMS_PROMOTIONS);

  return (
    <Wrapper>
      <SubTitle
        style={{
          fontSize: '12px',
          color: 'rgba(0,0,0,0.65)',
          fontWeight: 'bold',
        }}
      >
        {t(title)}:
        <strong style={{ fontWeight: 'lighter', fontSize: '13px' }}>{`${
          description ? ` (${description})` : ``
        }`}</strong>
      </SubTitle>
      {convertPromotion?.map((promotion, index) => (
        <PromotionOption
          key={promotion.promotionId || index}
          promotion={promotion}
          onClick={() => handlePromotionClick(promotion, index)}
        />
      ))}
      {promotions.length > MAX_ITEMS_PROMOTIONS && (
        <StyledSeeMoreView>
          <Flex direction="row" onClick={toggleShowFullPromotions}>
            <PromotionOptionLabel>
              {showFullPromotions
                ? t('Ẩn bớt')
                : t('Xem thêm {{numOfPromotion}} KM khác', {
                    numOfPromotion: promotions.length - MAX_ITEMS_PROMOTIONS,
                  })}
            </PromotionOptionLabel>
            {showFullPromotions ? (
              <UpOutlined color="#000000E0" size={20} />
            ) : (
              <DownOutlined color="#000000E0" size={20} />
            )}
          </Flex>
        </StyledSeeMoreView>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: #f2f5f5;
  border-radius: 4px;
`;

const StyledSeeMoreView = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 14px;
`;

export interface PromotionOptionProps {
  promotion: SelectionPromotion;
  onClick?: () => void;
}

const PromotionOption = ({ promotion, onClick }: PromotionOptionProps) => {
  const { isSelectable, isSelected } = promotion;

  return (
    <PromotionOptionWrapper
      onClick={!!isSelectable ? onClick : undefined}
      isSelected={!!isSelected}
    >
      <StyledPromotionOptionHeader>
        <PromotionIconWrapper>
          <img src={promotion.imageUrl} alt="icon" height={16} width={16} />
        </PromotionIconWrapper>
        <PromotionOptionLabel>{promotion.title}</PromotionOptionLabel>
      </StyledPromotionOptionHeader>
      <Divider
        dividerStyle={DividerStyleEnum.dashed}
        color="rgba(0,0,0,0.15)"
        gap="4px"
      />
      <StyledPromotionOptionBody>
        {!!promotion.highlightedDescriptions?.length && (
          <Body weight="regular" color="#F89E33">
            {promotion.highlightedDescriptions[0]}
          </Body>
        )}
        <ConditionText>{promotion.description}</ConditionText>
        <Flex marginTop="s8" justify="space-between" width="100%">
          <ExpiredAt>{`${t('HSD')}: ${new Date(
            promotion.endedAt ?? new Date(),
          ).toLocaleDateString('vi')}`}</ExpiredAt>
          <StyledTick isSelected={!!isSelected}>
            <CheckOutlined color="white" width={10} height={7} />
          </StyledTick>
        </Flex>
      </StyledPromotionOptionBody>
    </PromotionOptionWrapper>
  );
};

const maxLineCss = (maxLines: number) => `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${maxLines};`;

export interface StyledProps extends ThemeProps {
  isSelected?: boolean;
}

const PromotionOptionWrapper = styled.div(
  ({ isSelected, theme }: StyledProps) => `
  margin-top: 8px;
  background-color: white;
  width: 100%;
  padding: 8px 0px;
  border-radius: 8px;
  border: 1px solid
    ${isSelected ? theme?.colors.primary : 'rgba(0, 0, 0, 0.15)'};
`,
);
const StyledPromotionOptionHeader = styled.div`
  padding: 0px 12px;
  display: flex;
  flex-direction: row;
`;

const StyledPromotionOptionBody = styled.div`
  padding: 0px 12px;
`;

const PromotionIconWrapper = styled.div`
  width: 7%;
  display: flex;
  justify-content: start;
  align-items: center;
`;

const PromotionOptionLabel = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  ${maxLineCss(3)}
  color: #1B1D29;
  white-space: pre-line;
  margin-right: 4px;
`;

const ConditionText = styled.div`
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: #434657;
  ${maxLineCss(2)}
`;

const ExpiredAt = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: #434657;
`;

const StyledTick = styled.div(
  ({ isSelected, theme }: StyledProps) =>
    `
      width: 24px;
      height: 24px;
      border-radius: 999px;
      background-color: ${isSelected ? theme?.colors.primary : 'white'};
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid ${
        isSelected ? theme?.colors.primary : 'rgba(0, 0, 0, 0.15)'
      };

`,
);
